import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useWebsiteBuilder } from '../context/WebsiteBuilderContext';
import { generatePageHTML, determineInitialPageId, isPageGeneratedOrHasContent } from '../../../utils/htmlGenerator';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';
import { usePageTracking } from '../../../hooks/usePageTracking';

/**
 * WebsitePreviewPage — a dedicated full-screen preview page at /website-builder/preview.
 * Real route with a shareable URL. Shows the live website preview with tabs for all sitemap pages.
 * If a page has not been generated yet, a clean "Page not generated yet" message is displayed.
 */
export default function WebsitePreviewPage() {
  const navigate = useNavigate();
  const {
    pages,
    nodes,
    theme,
    brandName,
    brandLogo,
    globalContactInfo,
    activePageId,
    isCheckingSession,
    isSessionReady,
  } = useWebsiteBuilder();

  const { trackAction } = useTelemetry({ module: MODULES.WEBSITE_BUILDER, submodule: 'Website Preview' });
  usePageTracking({ moduleName: 'Website Builder', submoduleName: 'Preview', routePath: '/website-builder/preview' });

  // Include all sitemap-enabled pages so tabs and navbar show the full site structure
  const displayPages = useMemo(() => {
    if (nodes && nodes.length > 0) {
      const enabledIds = new Set(nodes.filter(n => n.enabled !== false).map(n => n.id));
      const filtered = pages.filter(p => enabledIds.has(p.pageId));
      return filtered.length > 0 ? filtered : pages;
    }
    return pages;
  }, [pages, nodes]);

  // Check if the project has at least one page with content
  const hasAnyGeneratedContent = useMemo(() => {
    return displayPages.some(p => isPageGeneratedOrHasContent(p, displayPages));
  }, [displayPages]);

  // Determine the default active page (preferring generated pages, defaulting to Home)
  const initialPage = useMemo(() => {
    if (displayPages.length === 0) return 'home';
    return determineInitialPageId(displayPages, activePageId);
  }, [displayPages, activePageId]);

  const [currentPageId, setCurrentPageId] = useState<string>(initialPage);
  const [historyStack, setHistoryStack] = useState<string[]>([initialPage]);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  useEffect(() => {
    trackAction(ACTIONS.PREVIEW_WEBSITE, 'Preview Website (Dedicated Page)');
  }, [trackAction]);

  // Keep currentPageId in sync if displayPages change
  useEffect(() => {
    if (displayPages.length > 0 && !displayPages.some(p => p.pageId === currentPageId)) {
      const nextId = determineInitialPageId(displayPages, activePageId);
      setCurrentPageId(nextId);
      setHistoryStack([nextId]);
    }
  }, [displayPages, currentPageId, activePageId]);

  // Reset iframe loading state when page changes
  useEffect(() => {
    setIframeLoaded(false);
  }, [currentPageId]);

  // Compute sanitized HTML for current page
  const sanitizedHTML = useMemo(() => {
    if (displayPages.length === 0) return '';
    let page = displayPages.find(p => p.pageId === currentPageId);
    if (!page) {
      page = displayPages.find(p => p.pageId === initialPage) || displayPages[0];
    }
    if (!page) return '';

    try {
      const rawHTML = generatePageHTML(page, theme, displayPages, brandName, brandLogo, globalContactInfo, true);
      return DOMPurify.sanitize(rawHTML, {
        ADD_TAGS: ['iframe', 'style', 'script', 'link', 'meta', 'head', 'html', 'title'],
        ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target', 'onclick', 'data-nav-id', 'onmouseover', 'onmouseout', 'href', 'rel', 'data-post'],
        WHOLE_DOCUMENT: true,
      });
    } catch (err) {
      console.error('Preview Render Error:', err);
      return `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#666;">
        <h2 style="color:#ef4444;margin-bottom:8px;">Preview failed to load</h2>
        <p>Please return to the editor and try again.</p>
      </div>`;
    }
  }, [currentPageId, displayPages, theme, brandName, brandLogo, globalContactInfo, initialPage]);

  // Handle intra-preview iframe navigation and messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NAVIGATE' && event.data?.pageId) {
        let targetPageId = event.data.pageId;
        if (targetPageId.endsWith('.html')) {
          targetPageId = targetPageId.replace('.html', '');
          if (targetPageId === 'index') targetPageId = 'home';
        }
        if (displayPages.some(p => p.pageId === targetPageId)) {
          setCurrentPageId(targetPageId);
          setHistoryStack(prev => [...prev, targetPageId]);
        }
      } else if (event.data?.type === 'CLOSE_PREVIEW') {
        navigate('/website-builder/editor');
      } else if (event.data?.type === 'GO_BACK') {
        setHistoryStack(prev => {
          if (prev.length > 1) {
            const next = prev.slice(0, -1);
            setCurrentPageId(next[next.length - 1]);
            return next;
          } else {
            navigate('/website-builder/editor');
            return prev;
          }
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [displayPages, navigate]);

  // Format clean brand domain for the address bar
  const displayDomain = useMemo(() => {
    const clean = (brandName || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    return clean ? `${clean}.com` : 'mywebsite.com';
  }, [brandName]);

  // State 1: Still checking session or waiting for initial data
  if (isCheckingSession || !isSessionReady) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#1a1a2e] flex flex-col items-center justify-center text-white font-sans">
        <div className="flex flex-col items-center max-w-sm px-6 text-center animate-fade-in">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={brandName || 'Brand'}
              className="h-14 w-auto max-w-[200px] object-contain mb-6 rounded-lg bg-white/10 p-2"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6 shadow-inner">
              <i className="ri-layout-masonry-line text-3xl text-indigo-400"></i>
            </div>
          )}
          <div className="w-8 h-8 border-3 border-indigo-400/20 border-t-indigo-400 rounded-full animate-spin mb-4"></div>
          <h2 className="text-xl font-semibold mb-2 tracking-tight">Preparing Website Preview</h2>
          <p className="text-sm text-gray-400">
            {brandName ? `Loading pages and styles for ${brandName}...` : 'Loading pages and styles...'}
          </p>
        </div>
      </div>
    );
  }

  // State 2: No generated content anywhere in the project
  if (!hasAnyGeneratedContent) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#f8fafc] flex flex-col items-center justify-center p-6 text-center font-sans">
        <div className="max-w-md bg-white rounded-2xl p-8 shadow-xl border border-gray-100 flex flex-col items-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center mb-4">
            <i className="ri-file-warning-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">No Website Content Available</h2>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            {brandName ? `${brandName} does` : 'This project does'} not have any generated website pages yet. Open the Website Builder to generate and edit pages.
          </p>
          <button
            onClick={() => navigate('/website-builder/editor')}
            className="px-6 py-2.5 bg-[#0859b8] hover:bg-[#0859b8]/90 text-white font-medium rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-edit-line"></i>
            <span>Go to Website Builder</span>
          </button>
        </div>
      </div>
    );
  }

  // State 3: Ready with pages
  return (
    <div className="fixed inset-0 z-[100] bg-white overflow-hidden flex flex-col">
      {/* Browser chrome bar */}
      <div className="flex-shrink-0 bg-[#1a1a2e] px-4 py-2.5 flex items-center gap-3 relative z-10 border-b border-white/10 shadow-sm">
        {/* Page tabs — shows all pages in the sitemap */}
        <div className="flex-1 flex items-center gap-1 overflow-x-auto scrollbar-hide py-0.5">
          {displayPages.map(p => (
            <button
              key={p.pageId}
              onClick={() => {
                setCurrentPageId(p.pageId);
                setHistoryStack(prev => [...prev, p.pageId]);
              }}
              className={`px-3.5 py-1.5 text-xs rounded-md whitespace-nowrap transition-all cursor-pointer ${
                currentPageId === p.pageId
                  ? 'bg-white/20 text-white font-medium shadow-sm'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/10'
              }`}
            >
              {p.pageName}
            </button>
          ))}
        </div>

        {/* Address bar */}
        <div className="bg-[#2a2a3e] rounded-lg px-3 py-1.5 text-xs text-gray-300 flex items-center gap-2 w-64 flex-shrink-0 border border-white/5">
          <i className="ri-lock-line text-[11px] text-emerald-400"></i>
          <span className="truncate">
            {displayDomain}/{currentPageId === 'home' ? '' : currentPageId}
          </span>
        </div>

        {/* Share Preview Button */}
        <button
          onClick={handleCopyLink}
          className="bg-[#0859b8] hover:bg-[#0859b8]/90 text-white text-xs px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap shadow-sm font-medium"
          title="Copy shareable preview URL"
        >
          <i className={copied ? 'ri-checkbox-circle-line text-emerald-300' : 'ri-share-line'}></i>
          <span>{copied ? 'Link Copied!' : 'Share Preview'}</span>
        </button>

        {/* Back to Editor Button */}
        <button
          onClick={() => navigate('/website-builder/editor')}
          className="text-gray-400 hover:text-white text-xs px-3 py-1.5 rounded-lg hover:bg-white/10 flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back to Editor</span>
        </button>
      </div>

      {/* Preview Canvas Container */}
      <div className="flex-1 relative bg-gray-100 overflow-hidden">
        {/* Smooth loading overlay — visible only until iframe DOM is ready */}
        {!iframeLoaded && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gray-50/90 backdrop-blur-sm transition-opacity duration-300">
            <div className="w-10 h-10 border-3 border-[#0859b8]/20 border-t-[#0859b8] rounded-full animate-spin mb-3"></div>
            <p className="text-gray-600 font-medium text-sm">
              Loading {displayPages.find(p => p.pageId === currentPageId)?.pageName || 'page'}...
            </p>
          </div>
        )}

        {/* Iframe with smooth transition on load */}
        <iframe
          key={currentPageId}
          srcDoc={sanitizedHTML}
          className={`w-full h-full border-0 transition-opacity duration-300 ${
            iframeLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          title="Website Preview"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          onLoad={() => {
            setIframeLoaded(true);
          }}
        />
      </div>
    </div>
  );
}
